<template>
  <SearchResult
    v-if="route.query.searchQuery"
    :is-pending="'success' !== status"
    :category-slider="data.categorySlider"
  />
  <template v-else-if="undefined !== category">
    <Breadcrumbs :breadcrumbs="breadcrumbs" />
    <Promotions
      v-if="category.code === promotionCategoryCode"
      :category="category"
    />
    <template v-else>
      <div class="product-catalog">
        <PromotionBanner
          v-if="category.parent?.code === promotionCategoryCode"
          :category="category"
        />
        <template v-else>
          <h1 class="product-catalog__title">
            {{ data.selections?.title ?? category.getName() }}
          </h1>
        </template>
        <CategoryShowcase
          v-if="children(category).length > 0"
          :categories="children(category)"
        />
      </div>
      <ProductCatalog
        :is-pending="'success' !== status"
        :selections-description="data.selections?.description"
        :category-slider="data.categorySlider"
      />
    </template>
  </template>
</template>

<script setup>
import Breadcrumbs from "~/modules/shared/breadcrumbs/Breadcrumbs.vue";
import { useCategoriesStore } from "~/store/categoriesStore";
import ProductCatalog from "~/modules/productCatalog/ui/components/ProductCatalog.vue";
import { useProductStore } from "~/store/productStore";
import { useCriteriaStore } from "~/store/criteriaStore";
import { promotionCategoryCode } from "~/config/category.config";
import Promotions from "~/modules/productCatalog/ui/pages/promotions/Promotions.vue";
import PromotionBanner from "~/modules/productCatalog/ui/components/PromotionBanner.vue";
import { useFilterStore } from "~/store/filterStore";
import SearchResult from "~/modules/productCatalog/ui/pages/search/SearchResult.vue";
import CategoryShowcase from "~/modules/productCatalog/ui/components/CategoryShowcase.vue";
import { useStrapiApi } from "~/uses/useMyFetch";
import {
  getCategorySliderQuery,
  getSelectionsQuery,
} from "~/utils/strapiQuery.utils";
import { specificationsStringify } from "~/config/filterParam.config";

definePageMeta({
  key: "category",
});

// Подключение сторов
const route = useRoute();
const categoryStore = useCategoriesStore();
const filterStore = useFilterStore();
const criteriaStore = useCriteriaStore();
const productStore = useProductStore();

const { getChildren: children } = storeToRefs(categoryStore);

const { totalItems } = storeToRefs(productStore);

const category = computed(() => {
  return categoryStore.getCategoryBySlug(route.params.categorySlug);
});

const breadcrumbs = ref([]);

const buildBreadcrumbs = (category) => {
  breadcrumbs.value.unshift({
    name: category.name,
    routeName: "category",
    params: {
      categorySlug: category.slug,
    },
  });

  if (null !== category.parent) {
    return buildBreadcrumbs(category.parent);
  }

  breadcrumbs.value.unshift({
    name: _T("@Catalog"),
    routeName: "catalog",
  });
};

const getJsonLd = (category, criteria) => {
  const getAggregateRating = (criteria) => {
    if (criteria.reviews?.count > 0) {
      return {
        "@type": "AggregateRating",
        ratingValue: criteria.reviews.averageRating,
        reviewCount: criteria.reviews.count,
      };
    }
  };

  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name: category.getName(),
    image: category.getImageUrl("catalogImage"),
    offers: {
      "@type": "AggregateOffer",
      availability: "https://schema.org/InStock",
      lowPrice: criteria?.priceRange.min ?? 1,
      highPrice: criteria?.priceRange.max ?? 1,
      priceCurrency: "UAH",
      offerCount: totalItems.value,
    },
    aggregateRating: getAggregateRating(criteria),
  };
};

watch(
  [() => route.params.categorySlug, () => route.query.searchQuery],
  async () => {
    breadcrumbs.value = [];
    if (undefined === route.query.searchQuery) {
      buildBreadcrumbs(category.value);
    }
    filterStore.initParams();
  },
  { immediate: true },
);

const { status, data } = await useAsyncData(
  "fetchProductCatalogData",
  async () => {
    const [selections, categorySlider] = await Promise.all([
      new Promise((resolve) => {
        if (
          undefined === category.value ||
          0 === route.params.specifications.length
        ) {
          return resolve({ data: null });
        }

        return resolve(
          useStrapiApi(
            `/specification/selections?${getSelectionsQuery(
              category.value.code,
            )}`,
            {
              timeout: 1000,
            },
          ).catch(() => {
            return {
              data: null,
            };
          }),
        );
      }),
      useStrapiApi(`/sliders/categories?${getCategorySliderQuery()}`),
      new Promise((resolve) => resolve(productStore.fetch())),
      new Promise((resolve) => resolve(criteriaStore.fetch())),
      new Promise((resolve) =>
        undefined !== category.value
          ? resolve(categoryStore.fetchContent(category.value.id))
          : resolve(),
      ),
    ]);

    return {
      selections: selections.data,
      categorySlider: categorySlider.data,
    };
  },
  { watch: [() => route.params, () => route.query] },
);

watch(
  () => data.value,
  () => {
    if (import.meta.server) {
      const filterSpecifications = filterStore.params.specifications;
      const criteriaSpecifications = criteriaStore.criteria.specifications;

      const sanitizedSpecifications = {};

      for (const specificationSlug in filterSpecifications) {
        const validSpecification = criteriaSpecifications.find(
          ({ slug }) => slug === specificationSlug,
        );

        if (undefined === validSpecification) {
          continue;
        }

        const validOptions = filterSpecifications[specificationSlug].filter(
          (optionSlug) =>
            validSpecification.options.some(({ slug }) => slug === optionSlug),
        );

        if (0 !== validOptions.length) {
          sanitizedSpecifications[specificationSlug] = validOptions;
        }
      }

      const stringifiedSpecifications = specificationsStringify(
        sanitizedSpecifications,
      );

      if (stringifiedSpecifications !== route.params.specifications) {
        navigateTo(
          {
            name: route.name,
            params: {
              ...route.params,
              specifications: stringifiedSpecifications,
            },
          },
          {
            redirectCode: 301,
          },
        );
      }
    }
  },
  {
    immediate: true,
  },
);

watch(
  [() => data.value.selections, () => category.value],
  () => {
    const withoutIndex = computed(() => {
      return (
        undefined === category.value ||
        (0 !== route.params.specifications.length &&
          null === data.value.selections)
      );
    });

    if (undefined !== category.value) {
      configureCategorySeo(category.value, data.value.selections);
    }

    useSeoMeta({
      robots: () =>
        withoutIndex.value ? "noindex, nofollow" : "index, follow",
    });
  },
  {
    immediate: true,
  },
);

if (undefined !== category.value && import.meta.server) {
  useHead({
    script: [
      {
        type: "application/ld+json",
        children: getJsonLd(category.value, criteriaStore.criteria),
      },
    ],
  });
}
</script>
